import { createStorageSlot } from '@gonfalon/browser-storage/createStorageSlot';
import { isSafeInternalReturnPath } from '@gonfalon/navigator/isSafeInternalPath';
import { z } from 'zod';

const OAUTH_AUTHORIZE_PATH = '/trust/oauth/authorize';
const MAX_AGE_MS = 30 * 60 * 1000;

const storedReturnPathSchema = z.object({ path: z.string(), ts: z.number() });

export const signupOAuthReturnPathSlot = createStorageSlot('signup-oauth-return-path', {
  storageType: 'localStorage',
});

export function toOAuthAuthorizeReturnPath(raw: string | null | undefined): string | undefined {
  if (!raw || !isSafeInternalReturnPath(raw)) {
    return undefined;
  }

  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname !== OAUTH_AUTHORIZE_PATH) {
      return undefined;
    }
    return url.pathname + url.search;
  } catch {
    return undefined;
  }
}

export function captureSignupOAuthReturnPath(redirect: string | null): void {
  const path = toOAuthAuthorizeReturnPath(redirect);
  if (path) {
    signupOAuthReturnPathSlot.set(JSON.stringify({ path, ts: Date.now() }));
  }
}

export function clearSignupOAuthReturnPath(): void {
  signupOAuthReturnPathSlot.remove();
}

function parseStoredReturnPath(raw: string): z.infer<typeof storedReturnPathSchema> | undefined {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return undefined;
  }
  const result = storedReturnPathSchema.safeParse(json);
  return result.success ? result.data : undefined;
}

export function consumeSignupOAuthReturnPath(): string | undefined {
  const raw = signupOAuthReturnPathSlot.get();
  signupOAuthReturnPathSlot.remove();
  if (!raw) {
    return undefined;
  }

  const stored = parseStoredReturnPath(raw);
  if (!stored || Date.now() - stored.ts >= MAX_AGE_MS) {
    return undefined;
  }
  return toOAuthAuthorizeReturnPath(stored.path);
}
